import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

/**
 * Chapter 12: Transcendence
 * Spiral path rising upward with tube geometry
 * Dark sphere at bottom, bright sphere at top
 * Purple gradient, light intensity increases with audio
 */
export default function AscendingSpiral({ audioData, isActive, position = [0, 0, 0] }) {
  const spiralRef = useRef()
  const bottomSphereRef = useRef()
  const topSphereRef = useRef()
  const particlesRef = useRef()

  const { fft, bass, mids, highs, amplitude, beat } = audioData || {
    fft: new Float32Array(128).fill(0),
    bass: 0,
    mids: 0,
    highs: 0,
    amplitude: 0,
    beat: 0
  }

  // Create ascending spiral path
  const spiralPath = useMemo(() => {
    const points = []
    const turns = 4
    const height = 6
    const radius = 1.5

    for (let i = 0; i <= 200; i++) {
      const t = i / 200
      const angle = t * turns * Math.PI * 2
      const y = (t - 0.5) * height

      const x = Math.cos(angle) * radius * (1 - t * 0.3) // Spiral inward as it rises
      const z = Math.sin(angle) * radius * (1 - t * 0.3)

      points.push(new THREE.Vector3(x, y, z))
    }

    return new THREE.CatmullRomCurve3(points)
  }, [])

  // Particles ascending along spiral
  const particleCount = 100
  const particleProgress = useRef(new Float32Array(particleCount))

  // Initialize particle progress
  useMemo(() => {
    for (let i = 0; i < particleCount; i++) {
      particleProgress.current[i] = i / particleCount
    }
  }, [])

  useFrame((state, delta) => {
    if (!isActive) return

    const time = state.clock.elapsedTime

    if (spiralRef.current) {
      // Gentle rotation of entire spiral
      spiralRef.current.rotation.y += delta * 0.1

      // Color gradient along spiral (dark purple to bright purple)
      // This is handled by material properties
    }

    if (bottomSphereRef.current) {
      // Dark sphere at bottom pulses subtly
      const pulse = 1 + Math.sin(time * 0.6) * 0.1 + bass * 0.2
      bottomSphereRef.current.scale.setScalar(pulse)

      // Rotate
      bottomSphereRef.current.rotation.y += delta * 0.1
    }

    if (topSphereRef.current) {
      // Bright sphere at top pulses with amplitude
      const pulse = 1 + Math.sin(time * 0.8) * 0.15 + amplitude * 0.4
      topSphereRef.current.scale.setScalar(pulse)

      // Faster rotation
      topSphereRef.current.rotation.y += delta * 0.15

      // Emissive intensity increases with audio
      topSphereRef.current.material.emissiveIntensity = 2 + amplitude * 3 + highs * 2
    }

    // Animate ascending particles
    if (particlesRef.current) {
      const positions = particlesRef.current.geometry.attributes.position.array
      const colors = particlesRef.current.geometry.attributes.color.array

      for (let i = 0; i < particleCount; i++) {
        // Update progress (particles ascend)
        particleProgress.current[i] += delta * (0.1 + amplitude * 0.2)

        // Loop back to bottom when reaching top
        if (particleProgress.current[i] > 1) {
          particleProgress.current[i] = 0
        }

        // Get position on spiral
        const point = spiralPath.getPoint(particleProgress.current[i])
        positions[i * 3] = point.x
        positions[i * 3 + 1] = point.y
        positions[i * 3 + 2] = point.z

        // Color gradient from dark to bright purple
        const colorT = particleProgress.current[i]
        colors[i * 3] = 0.5 + colorT * 0.5 // R
        colors[i * 3 + 1] = 0.0 + colorT * 0.5 // G
        colors[i * 3 + 2] = 0.5 + colorT * 0.5 // B
      }

      particlesRef.current.geometry.attributes.position.needsUpdate = true
      particlesRef.current.geometry.attributes.color.needsUpdate = true
    }
  })

  const particleGeometry = useMemo(() => {
    const geometry = new THREE.BufferGeometry()

    const positions = new Float32Array(particleCount * 3)
    const colors = new Float32Array(particleCount * 3)

    for (let i = 0; i < particleCount; i++) {
      const point = spiralPath.getPoint(i / particleCount)
      positions[i * 3] = point.x
      positions[i * 3 + 1] = point.y
      positions[i * 3 + 2] = point.z

      // Initial color gradient
      const colorT = i / particleCount
      colors[i * 3] = 0.5 + colorT * 0.5
      colors[i * 3 + 1] = 0.0 + colorT * 0.5
      colors[i * 3 + 2] = 0.5 + colorT * 0.5
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))

    return geometry
  }, [spiralPath])

  return (
    <group position={position}>
      {/* Spiral tube */}
      <mesh ref={spiralRef}>
        <tubeGeometry args={[spiralPath, 200, 0.1, 16, false]} />
        <meshStandardMaterial
          color="#8B00FF"
          emissive="#8B00FF"
          emissiveIntensity={0.6}
          transparent
          opacity={0.8}
          metalness={0.5}
          roughness={0.3}
        />
      </mesh>

      {/* Bottom sphere (dark) */}
      <mesh ref={bottomSphereRef} position={[0, -3, 0]}>
        <sphereGeometry args={[0.4, 32, 32]} />
        <meshStandardMaterial
          color="#4B0082"
          emissive="#4B0082"
          emissiveIntensity={0.3}
          transparent
          opacity={0.9}
        />
      </mesh>

      {/* Top sphere (bright) */}
      <mesh ref={topSphereRef} position={[0, 3, 0]}>
        <sphereGeometry args={[0.5, 32, 32]} />
        <meshStandardMaterial
          color="#E0B0FF"
          emissive="#E0B0FF"
          emissiveIntensity={2}
          transparent
          opacity={0.95}
        />
      </mesh>

      {/* Ascending particles */}
      <points ref={particlesRef} geometry={particleGeometry}>
        <pointsMaterial
          size={0.06}
          vertexColors
          transparent
          opacity={0.9}
          sizeAttenuation
          blending={THREE.AdditiveBlending}
        />
      </points>

      {/* Lighting */}
      <pointLight position={[0, -3, 0]} intensity={1} color="#4B0082" distance={4} />
      <pointLight position={[0, 3, 0]} intensity={3} color="#E0B0FF" distance={6} />

      {/* Gradient ambient light along spiral */}
      <pointLight position={[0, 0, 0]} intensity={1.5} color="#8B00FF" distance={8} />
    </group>
  )
}
